import { AlertTriangle, CalendarX2, PackageX } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { getCancelSaleImpactV2, type CancelSaleImpactDto } from '@/lib/api/v2/projects'

interface CancelSaleDialogProps {
  projectId: string
  /**
   * True when the sale is backed by at least one accepted quote. Drives the copy only: a
   * quote-backed sale leaves its quotes behind (they simply stop being accepted), a DIRECT sale
   * leaves nothing at all. See the header comment for why the two cases must not share a sentence.
   */
  hasAcceptedQuotes: boolean
  isSubmitting: boolean
  onConfirm: (reason: string) => void
  onCancel: () => void
}

/**
 * SOLY-ORDER-002 v2 — cancel-sale confirmation with informed consent. The dialog loads the
 * cancel-impact dry-run and lists BEFORE the action what the cascade will touch — upcoming RDVs
 * (client-confirmed ones highlighted: call the client!), supplier lines, orders already sent. If
 * the dry-run fails the dialog stays usable with the static supplier caveat.
 *
 * The copy PROMISES NOTHING (decision Thierry 2026-08-13). It used to name « Restaurer la vente »
 * as the way back — that gesture died with the vidage (fiches 18/19), so the sentence pointed at a
 * button that does not exist. Re-accepting a quote does rebuild a sale, but we no longer sell it as
 * a safety net either: the modal states the resulting state and stops.
 *
 * The two cases cannot share one sentence without lying about one of them. A quote-backed sale is
 * recoverable in practice (its quotes survive, unaccepted); a DIRECT sale is not — its lines are
 * gone and there is nothing to re-accept, which is assumed and NOT a bug (fiche 19, same decision).
 *
 * Do NOT name the badge the quote falls back to. The domain sets it to `issued`, which the UI
 * renders « Généré » (ProjectsIdPage.getQuoteStatusBadge, and MapStatusLabel BE-side) — an earlier
 * cut of this copy said « Envoyé » and would have pointed at a badge that does not exist, repeating
 * the very mistake this rewrite fixes. Describing the state ("plus acceptés") survives a relabel.
 */
export function CancelSaleDialog({ projectId, hasAcceptedQuotes, isSubmitting, onConfirm, onCancel }: CancelSaleDialogProps) {
  // Mounted only while open (parent renders conditionally), so fresh state per open — no reset effect.
  const [reason, setReason] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [impact, setImpact] = useState<CancelSaleImpactDto | null>(null)
  const [impactFailed, setImpactFailed] = useState(false)

  useEffect(() => {
    let disposed = false
    getCancelSaleImpactV2(projectId)
      .then((dto) => {
        if (!disposed) setImpact(dto)
      })
      .catch(() => {
        if (!disposed) setImpactFailed(true)
      })
    return () => {
      disposed = true
    }
  }, [projectId])

  const canConfirm = reason.trim().length > 0 && acknowledged && !isSubmitting

  const confirmedCount = impact?.upcomingEvents.filter((e) => e.clientConfirmed).length ?? 0
  const upcomingCount = impact?.upcomingEvents.length ?? 0
  const hasImpact = impact !== null && (upcomingCount > 0 || impact.cancellableSupplierLines > 0 || impact.sentSupplierOrders.length > 0)

  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="project-cancel-sale-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer la vente ?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                La vente sort du chiffre d'affaires, des statistiques et du planning. Le projet, lui,
                reste.{' '}
                <span className="font-medium text-foreground" data-testid="project-cancel-sale-aftermath">
                  {hasAcceptedQuotes
                    ? 'Ses lignes quittent la vente et ses devis ne sont plus acceptés.'
                    : 'Ses lignes sont perdues : une vente directe supprimée ne se récupère pas.'}
                </span>
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {hasImpact && (
            <div className="space-y-2 rounded-md border p-3 text-sm" data-testid="project-cancel-sale-impact">
              {upcomingCount > 0 && (
                <div className="flex items-start gap-2">
                  <CalendarX2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p>
                    {upcomingCount === 1 ? '1 RDV à venir sera annulé' : `${upcomingCount} RDV à venir seront annulés`}
                    {confirmedCount > 0 && (
                      <span className="font-medium text-amber-700" data-testid="project-cancel-sale-confirmed-events">
                        {' '}
                        — dont {confirmedCount === 1 ? '1 confirmé par le client, prévenez-le' : `${confirmedCount} confirmés par le client, prévenez-les`}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {impact !== null && impact.cancellableSupplierLines > 0 && (
                <div className="flex items-start gap-2">
                  <PackageX className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p>
                    {impact.cancellableSupplierLines === 1
                      ? '1 ligne fournisseur sera annulée'
                      : `${impact.cancellableSupplierLines} lignes fournisseur seront annulées`}
                  </p>
                </div>
              )}
              {impact !== null &&
                impact.sentSupplierOrders.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      Commande déjà envoyée ({o.supplierName}
                      {o.orderReference ? `, n° ${o.orderReference}` : ''}) — à annuler de votre côté.
                    </p>
                  </div>
                ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cancel-sale-reason">
              Motif de l'annulation <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancel-sale-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
              placeholder="Ex. : le client veut renégocier"
              rows={3}
              maxLength={1000}
              aria-required="true"
              data-testid="project-cancel-sale-reason"
            />
          </div>

          {impactFailed && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Si une commande fournisseur a déjà été envoyée, l'annuler ici{' '}
                <span className="font-medium">ne l'annule pas chez le fournisseur</span> — pensez à les contacter.
              </p>
            </div>
          )}

          <div className="flex items-start gap-2">
            <Checkbox
              id="cancel-sale-ack"
              checked={acknowledged}
              onCheckedChange={(c) => setAcknowledged(c === true)}
              disabled={isSubmitting}
              data-testid="project-cancel-sale-ack"
            />
            <Label htmlFor="cancel-sale-ack" className="text-sm font-normal leading-snug">
              Je comprends que la vente sort des statistiques et du planning
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isSubmitting} data-testid="project-cancel-sale-back-btn">
            Retour
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault() // keep the dialog open during the async call
              if (canConfirm) onConfirm(reason.trim())
            }}
            disabled={!canConfirm}
            data-testid="project-cancel-sale-confirm-btn"
          >
            {isSubmitting ? 'Suppression…' : 'Supprimer la vente'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
